import React from 'react';
import { Box } from '@mui/material';
import { TargetGroupType } from './use-get-benefit-types';
import { AssessmentInformationModel } from '../../../../api-service/clients';
import { DeepPartial, ServiceProviderAssignmentErrors } from './interfaces';
import { Cell } from '../../../../components/cell';
import { Row } from '../../../../components/row';
import { Editor, IEditorRef } from '../../../../components/editor';
import { PrimaryAndAdditionalTargetGroupSelector } from '../../../../components/multi-select/primary-and-additional-target-group-selector';

interface IAssessmentInformationProps {
  assessmentInformation: DeepPartial<AssessmentInformationModel>;
  isReadOnly: boolean;
  targetGroupTypes: TargetGroupType[];
  errors: Pick<ServiceProviderAssignmentErrors,
    'primaryAssessmentTopicId'
    | 'assessmentSummary'
    | 'citizenSupportNeedsAssessment'
    | 'citizenPerspectiveInfo'
    | 'citizenAssessmentResourceInfo'
    | 'challengesAndNeedsInfo'
    | 'citizenFutureAspirationsInfo'
    | 'citizenAssessmentPurposeInfo'
  >;
  onRichTextChange: (section: 'backgroundInformation' | 'coordinationDetails' | 'assessmentInformation', fieldName: string, event: unknown, editor: unknown) => void;
  onPrimaryTargetGroupChange: (value?: TargetGroupType) => void;
  onAdditionalTargetGroupsChange: (values: TargetGroupType[]) => void;
}

export function AssessmentInformation(props: IAssessmentInformationProps) {
  const {
    assessmentInformation,
    isReadOnly,
    targetGroupTypes,
    errors,
    onRichTextChange,
    onPrimaryTargetGroupChange,
    onAdditionalTargetGroupsChange,
  } = props;

  const {
    primaryAssessmentTopicId,
    additionalAssessmentTopicIds,
    assessmentSummaryRichText,
    citizenSupportNeedsAssessmentRichText,
    citizenPerspectiveInfoRichText,
    citizenAssessmentResourceInfoRichText,
    citizenAssessmentPurposeInfoRichText,
    challengesAndNeedsInfoRichText,
    citizenFutureAspirationsInfoRichText,
  } = assessmentInformation || {};

  const assessmentSummaryEditorRef = React.useRef<IEditorRef>(null);
  const citizenSupportNeedsEditorRef = React.useRef<IEditorRef>(null);
  const citizenPerspectiveEditorRef = React.useRef<IEditorRef>(null);
  const citizenResourceEditorRef = React.useRef<IEditorRef>(null);
  const citizenPurposeEditorRef = React.useRef<IEditorRef>(null);
  const challengesAndNeedsEditorRef = React.useRef<IEditorRef>(null);
  const citizenFutureAspirationsEditorRef = React.useRef<IEditorRef>(null);

  function getPaddingForOption(option?: TargetGroupType): number {
    if (option?.shortName) {
      return (option.shortName.length - 3) * 5;
    }
    return 0;
  }

  function renderTargetGroupOption(prop: object, option: TargetGroupType) {
    return (
      <li
        {...prop}
        key={option.id}
        style={{
          fontWeight: option.isSelectable ? undefined : 'bolder',
          fontSize: option.isSelectable ? undefined : '.9rem',
          opacity: 1,
        }}
      >
        <span style={{ paddingLeft: getPaddingForOption(option) }}>{option.displayName}</span>
      </li>
    );
  }

  return (
    <Box mt={1}>
      <PrimaryAndAdditionalTargetGroupSelector<TargetGroupType>
        primaryLabel="Borgers primære målgruppe"
        primaryValue={targetGroupTypes.find(x => x.id === primaryAssessmentTopicId)}
        additionalValues={targetGroupTypes.filter(x => additionalAssessmentTopicIds?.some((c: string) => c === x.id))}
        options={targetGroupTypes}
        getOptionLabel={option => option.displayName}
        renderOption={renderTargetGroupOption}
        filterOptions={(options, filterState) => {
          if (filterState.inputValue) {
            return options.filter(x => x.sortField && x.sortField.toLowerCase().indexOf(filterState.inputValue.toLowerCase()) >= 0);
          }
          return options;
        }}
        getOptionDisabled={x => !x.isSelectable}
        onPrimaryChange={onPrimaryTargetGroupChange}
        onAdditionalChange={onAdditionalTargetGroupsChange}
        variant="outlined"
        disabled={isReadOnly}
        primaryRequired
        primaryError={!!errors.primaryAssessmentTopicId}
        primaryErrorText={errors.primaryAssessmentTopicId}
      />
      <Row>
        <Cell size="full">
          <Editor
            label="Vurdering af borgers situation"
            value={assessmentSummaryRichText}
            initialValue={assessmentSummaryRichText}
            ref={assessmentSummaryEditorRef}
            onChange={(event, editor) => onRichTextChange('assessmentInformation', 'assessmentSummary', event, editor)}
            onBlur={(event, editor) => onRichTextChange('assessmentInformation', 'assessmentSummary', event, editor)}
            resizable
            selectorPrefix="assignment-assessment-summary-"
            disabled={isReadOnly}
          />
          {errors.assessmentSummary && <span style={{ color: '#d51a65', fontSize: '0.75rem' }}>{errors.assessmentSummary}</span>}
        </Cell>
      </Row>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <Editor
              label="Vurdering af borgers støttebehov og indsats"
              value={citizenSupportNeedsAssessmentRichText}
              initialValue={citizenSupportNeedsAssessmentRichText}
              ref={citizenSupportNeedsEditorRef}
              onChange={(event, editor) => onRichTextChange('assessmentInformation', 'citizenSupportNeedsAssessment', event, editor)}
              onBlur={(event, editor) => onRichTextChange('assessmentInformation', 'citizenSupportNeedsAssessment', event, editor)}
              resizable
              selectorPrefix="assignment-citizen-support-needs-"
              disabled={isReadOnly}
            />
            {errors.citizenSupportNeedsAssessment && <span style={{ color: '#d51a65', fontSize: '0.75rem' }}>{errors.citizenSupportNeedsAssessment}</span>}
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <Editor
              label="Borgers perspektiv på indsatsen"
              value={citizenPerspectiveInfoRichText}
              initialValue={citizenPerspectiveInfoRichText}
              ref={citizenPerspectiveEditorRef}
              onChange={(event, editor) => onRichTextChange('assessmentInformation', 'citizenPerspectiveInfo', event, editor)}
              onBlur={(event, editor) => onRichTextChange('assessmentInformation', 'citizenPerspectiveInfo', event, editor)}
              resizable
              selectorPrefix="assignment-citizen-perspective-"
              disabled={isReadOnly}
            />
            {errors.citizenPerspectiveInfo && <span style={{ color: '#d51a65', fontSize: '0.75rem' }}>{errors.citizenPerspectiveInfo}</span>}
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <Editor
              label="Borgers ressourcer i forhold til indsatsen"
              value={citizenAssessmentResourceInfoRichText}
              initialValue={citizenAssessmentResourceInfoRichText}
              ref={citizenResourceEditorRef}
              onChange={(event, editor) => onRichTextChange('assessmentInformation', 'citizenAssessmentResourceInfo', event, editor)}
              onBlur={(event, editor) => onRichTextChange('assessmentInformation', 'citizenAssessmentResourceInfo', event, editor)}
              resizable
              selectorPrefix="assignment-citizen-resource-"
              disabled={isReadOnly}
            />
            {errors.citizenAssessmentResourceInfo && <span style={{ color: '#d51a65', fontSize: '0.75rem' }}>{errors.citizenAssessmentResourceInfo}</span>}
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <Editor
              label="Borgers aktuelle støttebehov"
              value={challengesAndNeedsInfoRichText}
              initialValue={challengesAndNeedsInfoRichText}
              ref={challengesAndNeedsEditorRef}
              onChange={(event, editor) => onRichTextChange('assessmentInformation', 'challengesAndNeedsInfo', event, editor)}
              onBlur={(event, editor) => onRichTextChange('assessmentInformation', 'challengesAndNeedsInfo', event, editor)}
              resizable
              selectorPrefix="assignment-challenges-needs-"
              disabled={isReadOnly}
            />
            {errors.challengesAndNeedsInfo && <span style={{ color: '#d51a65', fontSize: '0.75rem' }}>{errors.challengesAndNeedsInfo}</span>}
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <Editor
              label="Borgers ønsker for fremtiden"
              value={citizenFutureAspirationsInfoRichText}
              initialValue={citizenFutureAspirationsInfoRichText}
              ref={citizenFutureAspirationsEditorRef}
              onChange={(event, editor) => onRichTextChange('assessmentInformation', 'citizenFutureAspirationsInfo', event, editor)}
              onBlur={(event, editor) => onRichTextChange('assessmentInformation', 'citizenFutureAspirationsInfo', event, editor)}
              resizable
              selectorPrefix="assignment-citizen-future-aspirations-"
              disabled={isReadOnly}
            />
            {errors.citizenFutureAspirationsInfo && <span style={{ color: '#d51a65', fontSize: '0.75rem' }}>{errors.citizenFutureAspirationsInfo}</span>}
          </Cell>
        </Row>
      </Box>
      <Box mt={1}>
        <Row>
          <Cell size="full">
            <Editor
              label="Indsatsformål"
              value={citizenAssessmentPurposeInfoRichText}
              initialValue={citizenAssessmentPurposeInfoRichText}
              ref={citizenPurposeEditorRef}
              onChange={(event, editor) => onRichTextChange('assessmentInformation', 'citizenAssessmentPurposeInfo', event, editor)}
              onBlur={(event, editor) => onRichTextChange('assessmentInformation', 'citizenAssessmentPurposeInfo', event, editor)}
              resizable
              selectorPrefix="assignment-citizen-purpose-"
              disabled={isReadOnly}
            />
            {errors.citizenAssessmentPurposeInfo && <span style={{ color: '#d51a65', fontSize: '0.75rem' }}>{errors.citizenAssessmentPurposeInfo}</span>}
          </Cell>
        </Row>
      </Box>
    </Box>
  );
}
